import axios from '@/utils/apiClient'

// Media URLs and processing state are supplied by the API.  Keeping this
// normalization here lets the CMS survive the Apache -> Nginx media move
// without scattering storage paths through components.

const ACTIVE_STATUSES = ['pending', 'queued', 'processing', 'running']
const SUCCESS_STATUSES = ['ready', 'succeeded', 'complete', 'completed']

export const MEDIA_STATUS_LABELS = {
  pending: 'Queued',
  queued: 'Queued',
  processing: 'Processing',
  running: 'Processing',
  ready: 'Ready',
  succeeded: 'Ready',
  complete: 'Ready',
  completed: 'Ready',
  partial: 'Partial',
  failed: 'Failed',
  idle: 'Not processed'
}

export function mediaRecord (record) {
  if (!record) return {}
  if (record.media || record.image_media || record.processing) {
    return record.media || record.image_media || record.processing
  }
  // A NetX selection has its own import status. Only treat it as media state
  // when it actually carries derivative data; an import can fail before an
  // ArtworkPhoto exists and therefore has no versions to retry.
  if (record.selection && (record.selection.media || record.selection.variants || record.selection.derivatives)) {
    return record.selection.media || record.selection
  }
  return {}
}

export function importedArtworkPhotoId (asset) {
  if (!asset) return null
  return (asset.artwork_photo && asset.artwork_photo.id) ||
    asset.artwork_photo_id ||
    asset.promoted_artwork_photo_id ||
    (asset.selection && asset.selection.artwork_photo_id) ||
    null
}

export function mediaStatus (record) {
  const media = mediaRecord(record)
  return String(media.status || record.processing_status || 'idle').toLowerCase()
}

export function mediaStatusLabel (record) {
  const status = mediaStatus(record)
  return MEDIA_STATUS_LABELS[status] || status
}

export function mediaActive (record) {
  return ACTIVE_STATUSES.includes(mediaStatus(record))
}

export function mediaReady (record) {
  const status = mediaStatus(record)
  if (SUCCESS_STATUSES.includes(status)) return true
  const media = mediaRecord(record)
  return !!(media.ready || media.all_ready)
}

export function mediaVariants (record) {
  const media = mediaRecord(record)
  const variants = media.variants || media.derivatives || []
  if (Array.isArray(variants)) return variants
  return Object.keys(variants).map(name => Object.assign({ name }, variants[name] || {}))
}

export function variantName (variant) {
  return variant && (variant.name || variant.variant || variant.kind)
}

export function failedMediaVariants (record) {
  return mediaVariants(record).filter(variant => {
    const status = String(variant.status || '').toLowerCase()
    return status === 'failed' || status === 'missing' || status === 'partial'
  })
}

export function failedMediaMessages (record) {
  const messages = failedMediaVariants(record).map(variant => {
    const name = variantName(variant) || 'image version'
    return `${name}: ${variant.error || variant.error_message || 'conversion failed'}`
  })
  const media = mediaRecord(record)
  if (messages.length === 0 && ['failed', 'partial'].includes(mediaStatus(record)) && media.error) {
    messages.push(media.error)
  }
  return messages
}

function variantMatches (variant, names) {
  const name = String(variantName(variant) || '').toLowerCase()
  return names.some(candidate => name === String(candidate).toLowerCase())
}

function usableVariant (variant) {
  const status = String((variant && variant.status) || '').toLowerCase()
  return !!variant && !!(variant.url || variant.signed_url || variant.href) &&
    (!status || SUCCESS_STATUSES.includes(status))
}

export function mediaUrl (record, names) {
  const media = mediaRecord(record)
  const requested = Array.isArray(names) ? names : [names]
  const variants = mediaVariants(record)
  const variant = variants.find(item => variantMatches(item, requested) && usableVariant(item))
  if (variant) return variant.url || variant.signed_url || variant.href

  const directKeys = requested.reduce((keys, name) => keys.concat([
    `${name}_url`, `${name}_signed_url`, name
  ]), [])
  for (let index = 0; index < directKeys.length; index += 1) {
    const value = media[directKeys[index]]
    if (typeof value === 'string' && value.length > 0) return value
  }
  return null
}

export function thumbnailUrl (asset) {
  return mediaUrl(asset, ['thumbnail', 'preview', 'netx_thumbnail']) ||
    (asset && (asset.thumbnail_url || asset.preview_url)) || null
}

export function cmsImageUrl (photo) {
  return mediaUrl(photo, ['cms_big', 'cms_large', 'big', 'cms_1280', 'authenticated'])
}

export function artworkTopPhoto (artwork) {
  if (!artwork || !artwork.top_photo_id) return null

  if (artwork.top_photo && Number(artwork.top_photo.id) === Number(artwork.top_photo_id)) {
    return artwork.top_photo
  }

  const photos = Array.isArray(artwork.artwork_photos) ? artwork.artwork_photos : []
  return photos.find(photo => Number(photo.id) === Number(artwork.top_photo_id)) || null
}

export function artworkImageUrl (
  artwork,
  names,
  apiServer,
  fallbackFileServer,
  fallbackSuffix = 'th_big'
) {
  if (!artwork || !artwork.top_photo_id) return null

  return artworkPhotoImageUrl(
    artworkTopPhoto(artwork),
    artwork.id,
    names,
    apiServer,
    fallbackFileServer,
    fallbackSuffix
  )
}

export function artworkPhotoImageUrl (
  photo,
  artworkId,
  names,
  apiServer,
  fallbackFileServer,
  fallbackSuffix = 'th_big'
) {
  if (!photo || !photo.id) return null

  const currentUrl = mediaUrl(photo, names)
  if (currentUrl) return normalizeMediaUrl(currentUrl, apiServer)

  const base = String(fallbackFileServer || '').replace(/\/$/, '')
  if (!base || !artworkId) return null
  return `${base}/data/artwork/${artworkId}/${photo.id}_${fallbackSuffix}.jpg`
}

export function publicImageUrl (photo, width) {
  const suffix = width ? `_${width}` : ''
  return mediaUrl(photo, [
    `catalogue${suffix}`,
    `public${suffix}`,
    `public_${width}`,
    `catalogue_${width}`
  ])
}

export function originalDownloadUrl (photo) {
  const media = mediaRecord(photo)
  return media.original_download_url || media.original_url || media.download_url ||
    photo.original_download_url || null
}

export function normalizeMediaUrl (url, apiServer) {
  if (!url || typeof url !== 'string') return null
  if (/^https?:\/\//i.test(url) || url.indexOf('//') === 0) return url
  return `${(apiServer || '').replace(/\/$/, '')}/${url.replace(/^\//, '')}`
}

export function canDownloadOriginal (user) {
  const roles = (user && user.roles) || []
  return !!(user && (user.admin === true || Number(user.admin) === 1)) ||
    roles.some(role => String(role.shortcut || role.role || '').toLowerCase() === 'file_d')
}

export function canRetryMedia (user) {
  return !!(user && (user.admin === true || Number(user.admin) === 1)) || !!(user && user.edit)
}

export function retryArtworkPhoto (photoId, variant) {
  const payload = variant ? { variant } : {}
  return axios.post(`/artwork_photos/${photoId}/retry_media`, payload)
}



// WEBPACK FOOTER //
// ./src/utils/artworkMedia.js